import { isTextEntry } from '@/lib/textFieldKeys';
import {
  useRef,
  useState,
  type KeyboardEvent,
  type MouseEvent,
  type RefObject,
} from 'react';
import { CopyIcon, FunnelIcon, FunnelXIcon } from '@phosphor-icons/react';
import { useGT, useLocale } from 'gt-react';
import {
  isColumnarSheet,
  isWorkbookErrorCell,
  WORKBOOK_PREVIEW_CELL_LENGTH,
  type WorkbookCell,
  type WorkbookColumnarSheet,
  type WorkbookSheet,
} from '@erp/shared-types';
import { resultColumns, sheetKind, type ColumnType } from '@erp/workbooks';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import type { ChangeSheet } from './useWorkbookEditor';
import { workbookCellText } from './workbookCellText';
import { inputColumnIsField } from './worksheetColumns';
import {
  filterByCell,
  type CellFilter,
  type NotePendingFilter,
} from './worksheetValueList';

/** The Worksheet a grid's Cell menu filters, when it can. */
export interface CellFilterTarget {
  sheet: WorkbookColumnarSheet;
  change: ChangeSheet;
  /** Whether the grid column is a Field of `sheet`, so filtering by its cells means the same thing. */
  filters: (column: string) => boolean;
  /** Told of each filter change, so the grid can hint at it before a Result shows it. */
  notePending?: NotePendingFilter;
}

/** Filters `sheet` by the cells of a grid of its Result: every column is an Output column. */
export function resultFilterTarget(
  sheet: WorkbookSheet | undefined,
  change: ChangeSheet | undefined,
  notePending?: NotePendingFilter,
): CellFilterTarget | undefined {
  // A pivot's cells are summaries, not Fields a filter can read.
  if (!isColumnarSheet(sheet) || !change) return undefined;
  return {
    sheet,
    change,
    filters: (column) => sheet.columns.some((item) => item.name === column),
    notePending,
  };
}

/**
 * Filters the Worksheet reading a Source by its preview's cells, only when
 * exactly one Worksheet reads it: otherwise which one is ambiguous.
 */
export function sourceFilterTarget(
  sheets: readonly WorkbookSheet[],
  sourceId: string,
  changeSheet: ((id: string) => ChangeSheet) | undefined,
): CellFilterTarget | undefined {
  const readers = sheets.filter(
    (sheet) => sheetKind(sheet).input() === sourceId,
  );
  const [sheet] = readers;
  if (!isColumnarSheet(sheet) || readers.length > 1 || !changeSheet)
    return undefined;
  return {
    sheet,
    change: changeSheet(sheet.id),
    filters: (column) => inputColumnIsField(sheet, column),
  };
}

/**
 * Declared types of a Worksheet's Result columns, so its Result shows as its
 * grid does. A pivot's come from its Result's columns when given.
 */
export function outputTypes(
  sheet: WorkbookSheet | undefined,
  columns?: readonly string[],
): ReadonlyMap<string, ColumnType> | undefined {
  return (
    sheet &&
    new Map(
      resultColumns(sheet, columns).map((column) => [column.name, column.type]),
    )
  );
}

/** The longest cell text a menu item quotes before cutting it. */
const QUOTED_LENGTH = 30;

/**
 * What filtering by a cell means: its value as the Field's value reads as
 * text, Empty, or its error. Null for a cell a preview cut, whose whole value
 * the grid doesn't have.
 */
export function cellFilterOf(cell: WorkbookCell): CellFilter | null {
  if (isWorkbookErrorCell(cell))
    return cell.error === '#N/A' ? { kind: 'na' } : { kind: 'errors' };
  if (cell === null || cell === '') return { kind: 'empty' };
  const value = String(cell);
  return value.length > WORKBOOK_PREVIEW_CELL_LENGTH
    ? null
    : { kind: 'value', value };
}

interface OpenMenu {
  cell: WorkbookCell;
  column: string;
  type?: ColumnType;
  x: number;
  y: number;
}

/**
 * The Cell menu of a grid: right-click a cell, or press the context-menu key
 * or Shift+F10 on it, to copy its value or, where `target` allows, filter by
 * it. Cells take focus one at a time (arrow keys move it), so the grid is one
 * Tab stop.
 */
export function useCellMenu(target?: CellFilterTarget) {
  const [open, setOpen] = useState<OpenMenu | null>(null);
  const [active, setActive] = useState({ row: 0, col: 0 });
  const cellElement = useRef<HTMLElement | null>(null);
  function show(menu: OpenMenu, element: HTMLElement) {
    cellElement.current = element;
    setOpen(menu);
  }
  function cellProps(
    row: number,
    col: number,
    cell: WorkbookCell,
    column: string,
    type?: ColumnType,
  ) {
    return {
      tabIndex: active.row === row && active.col === col ? 0 : -1,
      'data-cell': `${row}:${col}`,
      onFocus: () => setActive({ row, col }),
      onContextMenu: (event: MouseEvent<HTMLElement>) => {
        event.preventDefault();
        event.currentTarget.focus();
        show(
          { cell, column, type, x: event.clientX, y: event.clientY },
          event.currentTarget,
        );
      },
      onKeyDown: (event: KeyboardEvent<HTMLElement>) => {
        if (
          event.key === 'ContextMenu' ||
          (event.shiftKey && event.key === 'F10')
        ) {
          event.preventDefault();
          const rect = event.currentTarget.getBoundingClientRect();
          show(
            { cell, column, type, x: rect.left, y: rect.bottom },
            event.currentTarget,
          );
          return;
        }
        const step = ARROWS[event.key];
        // Arrows typed in a field inside the cell move its caret, not the cell.
        if (!step || isTextEntry(event.target)) return;
        const next = event.currentTarget
          .closest('table')
          ?.querySelector<HTMLElement>(
            `[data-cell="${row + step[0]}:${col + step[1]}"]`,
          );
        if (!next) return;
        event.preventDefault();
        next.focus();
      },
    };
  }
  const menu = (
    <CellMenu
      open={open}
      close={() => setOpen(null)}
      target={target}
      returnFocus={cellElement}
    />
  );
  return { cellProps, menu };
}

const ARROWS: Partial<Record<string, readonly [number, number]>> = {
  ArrowUp: [-1, 0],
  ArrowDown: [1, 0],
  ArrowLeft: [0, -1],
  ArrowRight: [0, 1],
};

interface FilterLabels {
  out: string;
  only: string;
  /** For an Error cell: keep only #N/A. */
  na?: string;
}

/** A cell's filter actions: out and to only, and to only #N/A for a #N/A cell. */
export function filterItems(
  filter: CellFilter,
  labels: FilterLabels,
): { key: string; label: string; keep: boolean; cell: CellFilter }[] {
  // Any Error cell filters in and out as errors; only #N/A has its own filter.
  const cell: CellFilter = filter.kind === 'na' ? { kind: 'errors' } : filter;
  return [
    { key: 'out', label: labels.out, keep: false, cell },
    { key: 'only', label: labels.only, keep: true, cell },
    ...(filter.kind === 'na' && labels.na
      ? [{ key: 'na', label: labels.na, keep: true, cell: filter }]
      : []),
  ];
}

function CellMenu({
  open,
  close,
  target,
  returnFocus,
}: {
  open: OpenMenu | null;
  close: () => void;
  target?: CellFilterTarget;
  returnFocus: RefObject<HTMLElement | null>;
}) {
  const gt = useGT();
  const locale = useLocale();
  const anchor = open
    ? { getBoundingClientRect: () => new DOMRect(open.x, open.y, 0, 0) }
    : undefined;
  const filter =
    open && target?.filters(open.column) ? cellFilterOf(open.cell) : null;
  function filterBy(cell: CellFilter, keep: boolean) {
    if (!open || !target) return;
    const { column } = open;
    const after = filterByCell(target.sheet.filters, column, cell, keep);
    if (after.length <= 30) target.notePending?.({ column, cell, keep }, after);
    target.change((latest) => {
      const filters = filterByCell(latest.filters, column, cell, keep);
      // The schema holds 30 filters at most.
      return filters.length > 30 ? latest : { ...latest, filters };
    });
    close();
  }
  const copyText =
    open && open.cell !== null
      ? workbookCellText(open.cell, open.type, locale)
      : '';
  const quoted =
    copyText.length > QUOTED_LENGTH
      ? `${copyText.slice(0, QUOTED_LENGTH)}…`
      : copyText;
  function cellFilterLabels(cell: CellFilter): FilterLabels {
    if (cell.kind === 'value')
      return {
        out: gt('Filter out "{value}"', {
          context: 'workbook cell menu: remove rows with this value',
          value: quoted,
        }),
        only: gt('Filter to only "{value}"', {
          context: 'workbook cell menu: keep only rows with this value',
          value: quoted,
        }),
      };
    if (cell.kind === 'empty')
      return {
        out: gt('Filter out empty', {
          context: 'workbook cell menu: remove rows with empty cells',
        }),
        only: gt('Filter to only empty', {
          context: 'workbook cell menu: keep only empty cells',
        }),
      };
    return {
      out: gt('Filter out errors', {
        context: 'workbook cell menu: remove rows with errors',
      }),
      only: gt('Filter to only errors', {
        context: 'workbook cell menu: keep only rows with errors',
      }),
      na: gt('Filter to only #N/A', {
        context: 'workbook cell menu: keep only #N/A cells',
      }),
    };
  }
  const items = filter ? filterItems(filter, cellFilterLabels(filter)) : [];
  return (
    <DropdownMenu
      open={open !== null}
      onOpenChange={(next) => {
        if (!next) close();
      }}
    >
      <DropdownMenuContent
        anchor={anchor}
        side="bottom"
        align="start"
        sideOffset={0}
        finalFocus={returnFocus}
        aria-label={gt('Cell actions', { context: 'workbook cell menu' })}
      >
        <DropdownMenuItem
          onClick={() => {
            void navigator.clipboard?.writeText(copyText);
            close();
          }}
        >
          <CopyIcon />
          {gt('Copy value', { context: 'workbook cell menu: copy the value' })}
        </DropdownMenuItem>
        {items.length > 0 && <DropdownMenuSeparator />}
        {items.map((item) => (
          <DropdownMenuItem
            key={item.key}
            onClick={() => filterBy(item.cell, item.keep)}
          >
            {item.keep ? <FunnelIcon /> : <FunnelXIcon />}
            {item.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
